import { HtmlTagDescriptor, RenderContext } from "./types";

export const injectTargets = ["head-prepend", "body-prepend", "head", "body"] as const;
export const injectTagNames = [
  "headprepend",
  "bodyprepend",
  "headappend",
  "bodyappend",
] as const;
export type InjectTargets = typeof injectTargets[number];

export const tagInjectNameMap = {
  [injectTargets[0]]: injectTagNames[0],
  [injectTargets[1]]: injectTagNames[1],
  [injectTargets[2]]: injectTagNames[2],
  [injectTargets[3]]: injectTagNames[3],
} as const

type TagMap = {
  [k in InjectTargets]: HtmlTagDescriptor[];
};

const positionStringMatch = injectTargets.reduce((acca, injectTo) => {
  const tagName = tagInjectNameMap[injectTo];
  acca[injectTo] = `<${tagName}></${tagName}>`;
  return acca;
}, {} as Record<InjectTargets, string>);

function createTagMap() {
  return injectTargets.reduce((acca, current) => {
    acca[current] = [];
    return acca;
  }, {} as Partial<TagMap>) as TagMap;
}

function renderTagToString(tag: HtmlTagDescriptor): string {
  if ("raw" in tag) {
    return tag.raw;
  } else {
    return `<${tag.name} ${Object.entries(tag.attrs || {})
      .map(([prop, value]) => `${prop}="${value}"`)
      .join(" ")}>${
      tag.children
        ? typeof tag.children === "object"
          ? tag.children.map(renderTagToString)
          : tag.children
        : ""
    }</${tag.name}>`;
  }
}

export function createHTMLInjectableTags() {
  return Object.entries(tagInjectNameMap).map(([injectTo, tag]) => {
    return {
      tag,
      injectTo,
    };
  }) as { injectTo: InjectTargets; tag: string }[];
}

export async function renderUrlToString<P extends RenderContext>({
  url,
  template,
  render,
  context,
}: {
  url: string;
  template: string;
  render: (url: string, context: P) => Promise<HtmlTagDescriptor[]>;
  context: P;
}): Promise<[string, HtmlTagDescriptor[], P]> {
  const tags = await render(url, context);

  const tagMap = createTagMap();

  for (const tag of tags) {
    if (tag.injectTo) {
      tagMap[tag.injectTo].push(tag);
      continue;
    }

    tagMap["body"].push(tag);
  }

  let html = template;

  injectTargets.forEach((injectTo) => {
    const matchString = positionStringMatch[injectTo];
    html = html.replace(
      matchString,
      tagMap[injectTo].map(renderTagToString).join("")
    );
  });

  return [html, tags, context];
}
